<template>
  <div
    class="product-card-v2"
    :class="cardClass"
    @mouseenter="$vssWidth > 769 ? (hovering = true) : false"
    @mouseleave="onMouseLeaveCard"
  >
    <component
      :is="mainComponent()"
      :to="to"
      class="d-block"
      @click.native="!isLink ? null : (loading = true)"
    >
      <div v-if="video" class="product-card-v2__video">
        <video-bunny
          :src="`https://iframe.mediadelivery.net/embed/${
            BUNNY_STREAM_FOLDERS[video.folder]
          }/${video.id}?autoplay=true&loop=true&muted=true`"
        />
      </div>

      <div v-else class="product-card-v2__image">
        <transition mode="in-out" name="product-card-image-switch">
          <lazy-image
            v-if="images.length > 0"
            :key="currentColor ? currentColor._key : 'default'"
            :images="images"
            :preload="preloadImage || isFirst"
            :force-show="preloadImage || isFirst"
            provider="sanity"
          />
        </transition>
      </div>
      <div class="product-card-v2__content text-color-tertiary">
        <div class="inner">
          <div
            v-if="isHome && productDocument.type === 'normal'"
            class="d-none d-md-block"
          >
            <p class="lh-1 text-md-base text-left mb-0-8">
              <span
                v-if="
                  productDocument.marketing_arguments_type === 'Texte' &&
                  productDocument.marketing_message
                "
              >
                {{ productDocument.marketing_message }}
              </span>
              <span v-else>
                {{ $t('preco_of_the_week') }}
              </span>
            </p>
          </div>
          <div v-else-if="isAgenda && isFirst" class="d-none d-md-block">
            <p
              v-if="productDocument.dates.start"
              class="lh-1 text-md-base text-left mb-0-8"
            >
              {{
                $t('lauch_day', {
                  day: $d(productDocument.dates.start, 'weekday'),
                })
              }}
            </p>
          </div>
          <div
            v-else-if="
              (!isAgenda || productDocument.dates.start) &&
              !stock &&
              !isGiftCard
            "
            class="d-flex mb-1"
            :class="{
              'justify-center': !isHome && !isFirst,
            }"
          >
            <div>
              <marketing-argument
                :marketing="{
                  type: productDocument.marketing_arguments_type,
                  tag:
                    productDocument.marketing_arguments_type !== 'auto' &&
                    productDocument.marketing_arguments_render_as
                      ? productDocument.marketing_arguments_render_as
                      : 'auto',
                  message: productDocument.marketing_message,
                }"
                :dates="productDocument.dates"
                :inventory="productInventory"
                :status="productDocument.status"
              />
            </div>
          </div>

          <p
            class="product-card-v2__name text-secondary mb-0-2"
            :class="{
              'text-center': !isHome && !isFirst && !soonInSell && !stock,
            }"
          >
            {{ productDocument.title }}
          </p>

          <div
            v-if="!isHome && !isAgenda && !isGiftCard"
            class="product-card-v2__price d-flex flex-wrap align-baseline mb-1 mt-0-6"
          >
            <div
              class="product-card__price d-flex align-center mb-0 flex-shrink-0"
              :class="{ 'mr-1 mr-md-2': distinctColors.length > 1 }"
            >
              <!-- <span v-if="!shopifyProduct" class="d-block">
                <strong
                  class="skeleton skeleton--bar d-inline-block mr-0-6"
                  style="width: 30px; height: 16px"
                ></strong>
                <strong
                  class="skeleton skeleton--bar d-inline-block"
                  style="width: 30px; height: 16px"
                ></strong>
              </span> -->
              <div class="product-card__price__inner d-block">
                <p
                  class="price d-flex flex-wrap align-baseline text-color-tertiary text-secondary mb-0 mr-0-6 mr-md-1"
                >
                  <span class="number pr-0-4"
                    >{{
                      productDocument.shopify_product.priceRange
                        .minVariantPrice
                    }}&nbsp;&euro;</span
                  >

                  <span
                    class="product-card__price__text text-small text-no-wrap"
                    >{{
                      $t(
                        showCompareAtPrice
                          ? 'product.in_preco'
                          : 'product.in_stock'
                      )
                    }}</span
                  >
                </p>

                <p
                  v-if="showCompareAtPrice"
                  class="price compare-at-price d-flex flex-wrap align-baseline text-color-tertiary text-secondary mb-0 mr-0-6 mr-md-1"
                >
                  <span class="number pr-0-4"
                    >{{
                      productDocument.shopify_product.compareAtPriceRange
                        .minVariantPrice
                    }}&nbsp;&euro;</span
                  >
                  <span
                    class="product-card__price__text text-small text-no-wrap"
                    >{{ $t('product.in_stock') }}</span
                  >
                </p>
              </div>
            </div>

            <transition
              v-if="distinctColors.length > 1"
              mode="out-in"
              @enter="colorEnter"
              @leave="colorLeave"
            >
              <div
                v-if="hovering"
                class="product-card__colors d-flex align-center flex-wrap"
              >
                <button
                  v-for="(color, index) in distinctColors"
                  :key="color._key"
                  type="button"
                  class="colors__matter"
                  :class="{ active: activeColor === index }"
                  :title="color.color_name"
                  @mouseenter="colorMatterMouseEnter(index)"
                  @click.prevent="goToColor(color._key)"
                >
                  <span
                    class="bg"
                    :style="`background-image: url(${getColorThumbnailBg(
                      color
                    )})`"
                  ></span>
                  <span class="visually-hidden">{{ color.color_name }}</span>
                </button>
              </div>
              <p
                v-else
                class="d-none d-md-block product-card__colors--number mb-0"
              >
                <span v-if="small">
                  <span class="d-inline d-md-none">
                    {{
                      $t('card.product.number-of-colors-small', {
                        number: distinctColors.length,
                      })
                    }}
                  </span>
                  <span class="d-none d-md-inline">
                    {{
                      $t('card.product.number-of-colors', {
                        number: distinctColors.length,
                      })
                    }}
                  </span>
                </span>
                <span v-else>
                  {{
                    $t('card.product.number-of-colors', {
                      number: distinctColors.length,
                    })
                  }}
                </span>
              </p>
            </transition>
          </div>
          <div
            v-else-if="!isLink"
            :class="{
              'pb-0-4 mb-1': !isFirst,
              'w-100 pb-md-0-4 mb-0-4 mb-md-1': isFirst,
            }"
          >
            <div v-if="isFirst">
              <p class="d-none d-md-block lh-1-2 pb-md-0-4">
                <span class="d-block">{{
                  $t('card.product.agenda.alert.1')
                }}</span>
                <span class="d-block">{{
                  $t('card.product.agenda.alert.2')
                }}</span>
              </p>

              <div class="d-flex align-center justify-center d-md-none">
                <p
                  v-if="distinctColors.length > 1"
                  class="text-small lh-1-2 mb-0 pr-1 pt-0-4"
                >
                  {{
                    $tc(
                      'card.product.number-of-colors-soon',
                      distinctColors.length,
                      {
                        number: distinctColors.length,
                      }
                    )
                  }}
                </p>

                <div class="d-flex justify-center pt-0-4">
                  <button
                    class="product-card-v2__agenda text-color-tertiary w-600"
                    @click.prevent="onAlertClick($event)"
                  >
                    <span class="d-flex align-center link--underline">
                      <icon name="bell" :full="isSubscribed" />
                      <span class="d-block ml-0-4">{{
                        $t(
                          isSubscribed
                            ? 'alert.tooltip.remove'
                            : 'alert.tooltip.create'
                        )
                      }}</span>
                    </span>
                  </button>
                </div>
              </div>
              <div v-if="$vssWidth > 796" class="d-none d-md-block">
                <create-alert
                  :product="{
                    productSlug: productDocument.uid,
                    productName: productDocument.name,
                    productId: productDocument.shopify_product.id,
                  }"
                  :origin="origin"
                  button-size="primary"
                  inline
                  @has-subscribe="hasSubscribe()"
                  @has-unsubscribe="hasUnSubscribe()"
                />
              </div>
            </div>
            <div v-else>
              <p
                v-if="!bisOnly && distinctColors.length > 1"
                class="text-small text-center lh-1-2 pt-0-2"
              >
                {{
                  $tc(
                    'card.product.number-of-colors-soon',
                    distinctColors.length,
                    {
                      number: distinctColors.length,
                    }
                  )
                }}
              </p>
              <div
                class="d-flex justify-center pt-0-4"
                :class="{
                  'mt-0-6': distinctColors.length === 0,
                }"
              >
                <button
                  class="product-card-v2__agenda text-color-tertiary w-600"
                  @click.prevent="onAlertClick($event)"
                >
                  <span
                    v-if="bisOnly"
                    class="d-flex align-center link--underline"
                  >
                    <icon :name="isSubscribed ? 'heart-full' : 'heart'" />
                    <span class="d-block ml-0-4">{{
                      $t(
                        isSubscribed
                          ? 'alert.tooltip.unlove'
                          : 'alert.tooltip.love'
                      )
                    }}</span>
                  </span>
                  <span v-else class="d-flex align-center link--underline">
                    <icon name="bell" :full="isSubscribed" />
                    <span class="d-block ml-0-4">{{
                      $t(
                        isSubscribed
                          ? 'alert.tooltip.remove'
                          : 'alert.tooltip.create'
                      )
                    }}</span>
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div v-if="isGiftCard">
            <p
              class="product-card__price d-flex justify-center align-center w-500 mb-0 flex-shrink-0"
            >
              <span v-if="!shopifyProduct" class="d-block">
                <strong
                  class="skeleton skeleton--bar d-inline-block mr-0-6"
                  style="width: 30px; height: 16px"
                ></strong>
                <strong
                  class="skeleton skeleton--bar d-inline-block"
                  style="width: 30px; height: 16px"
                ></strong>
              </span>
              <span v-else class="d-block">
                <strong>{{
                  $t('product.price_range', {
                    min: shopifyProduct.priceRange.min,
                    max: shopifyProduct.priceRange.max,
                  })
                }}</strong>
              </span>
            </p>
          </div>

          <div
            v-if="isLink && !stock && productDocument.button_label"
            class="mt-1 mb-0-6"
            :class="{
              'd-flex justify-center': !isFirst && !fromLineOfLinks,
              'd-flex justify-start': !isFirst && fromLineOfLinks,
              'd-none d-md-flex justify-start': isFirst && !isEternal,
              'd-flex justify-center justify-md-start': isFirst && isEternal,
            }"
          >
            <primary-button
              ref="link"
              type="button"
              :text="productDocument.button_label"
              with-arrow
            />
          </div>

          <!-- <div v-if="stock && availableSizes.length > 0">
            <p class="text-color-secondary-4 mt-0-6 mb-0-2">
              {{ $t('product.available_sizes') }}
            </p>

            <div
              class="product-card__sizes"
              :class="{
                refresh,
                'justify-md-end': isTextPositionLeft,
              }"
            >
              <div
                v-for="(size, index) in availableSizes"
                :key="index"
                class="product-card__size"
                :class="{
                  'unavailable w-400': !size.available,
                  'w-600': size.available,
                }"
              >
                <span class="text-uppercase">{{ size.label }}</span>
              </div>
            </div>
          </div> -->
        </div>

        <div
          v-if="showCountDown"
          class="product-card-v2__countdown d-flex d-md-block flex-direction-column align-center mt-1 mt-md-0"
        >
          <div class="d-flex justify-center justify-md-end flex-grow-1 mb-0-6">
            <div v-if="showCountDownEndsIn" class="tag tag--sun-and-white">
              <icon name="estimated-time" />
              <span>{{ $t('product.tag.last_before_end') }}</span>
            </div>
            <div
              v-else-if="showCountDownStartsIn"
              class="tag tag--yellow-and-secondary"
            >
              <icon name="calendar-2" />
              <span>{{ $t('product.tag.soon_preco') }}</span>
            </div>
          </div>

          <big-count-down
            v-if="showCountDownEndsIn"
            :date="productDocument.dates.end"
            home
          />
          <big-count-down
            v-else-if="showCountDownStartsIn"
            :date="productDocument.dates.start"
          />
        </div>
      </div>
    </component>
    <transition name="appear-top">
      <p v-if="subscribeError" class="text-color-error text-small text-right">
        {{ $t('product.alert.error') }}
      </p>
    </transition>

    <transition name="from-bottom" @after-enter="afterCreateAlertEnter">
      <create-alert
        v-if="isAlertOpen"
        :id="`alert-${productDocument.uid}`"
        :product="{
          productSlug: productDocument.uid,
          productName: productDocument.name,
          productId: productDocument.shopify_product.id,
        }"
        :title="
          productDocument.dates.start !== null
            ? $t('product.alert.highlight_title', {
                name: productDocument.title,
                date: $d(productDocument.dates.start, 'short'),
              })
            : $t('product.back-one-day')
        "
        :text="
          $t(
            showCompareAtPrice
              ? 'product.receive-alert-preorder'
              : 'product.receive-alert-stock'
          )
        "
        :origin="origin"
        button-size="primary"
        with-close
        @has-subscribe="hasSubscribe()"
        @has-unsubscribe="hasUnSubscribe()"
        @close="isAlertOpen = false"
      />
    </transition>

    <transition name="from-bottom">
      <div
        v-if="loading"
        class="d-flex align-center justify-center pb-1 pt-1 text-color-tertiary text-secondary product-card-v2__loading"
      >
        <span>{{ $t('loading') }}</span>
        <icon-loading class="ml-1" />
      </div>
    </transition>
  </div>
</template>

<script>
import NuxtSSRScreenSize from 'nuxt-ssr-screen-size'
import { differenceInDays, isFuture, isPast } from 'date-fns'
import { mapGetters, mapActions } from 'vuex'

import LazyImage from '@/components/Image/LazyImage'
import MarketingArgument from '@/components/Product/MarketingArgument.v2'
import Icon from '@/components/icons/Icon'
import IconLoading from '@/components/icons/Loading'
import PrimaryButton from '@/components/buttons/Primary'
import DefaultDiv from '@/components/div/Default'
import VideoBunny from '@/components/video/bunny'

import { BUNNY_STREAM_FOLDERS, ORIGIN, STATUS, TYPES } from '@/const'
import { EVENTS } from '@/const/events'
import { PRODUCT_TEXT_ALIGN } from '@/entities/prismic/Product'
import { getProductCardImageList, getSanityBunnyImageLink } from '~/utils/image'

export default {
  name: 'ProductCard',
  components: {
    LazyImage,
    MarketingArgument,
    Icon,
    IconLoading,
    PrimaryButton,
    VideoBunny,
    CreateAlert: () =>
      import(
        '@/components/Product/CreateAlert' /* webpackChunkName: 'product_create_alert' */
      ),
    BigCountDown: () =>
      import(
        '@/components/countdown/BigCountDown' /* webpackChunkName: 'big_countdown' */
      ),
  },
  mixins: [NuxtSSRScreenSize.NuxtSSRScreenSizeMixin],

  props: {
    productDocument: {
      type: Object,
      required: true,
    },
    shopifyProduct: {
      type: Object,
      default: null,
    },
    origin: {
      type: String,
      default: ORIGIN.AGENDA,
    },
    cardType: {
      type: String,
      default: 'grid',
    },
    typeMobile: {
      type: String,
      default: 'one-per-line',
    },
    typeDesktop: {
      type: String,
      default: 'one-per-line',
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    preloadImage: {
      type: Boolean,
      default: false,
    },
    isHome: { type: Boolean, default: false },
    stock: { type: Boolean, default: false },
    bisOnly: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    soonInSell: { type: Boolean, default: false },
    targetColor: { type: String, default: null },
    overrideImage: { type: Object, default: null },
    video: { type: Object, default: null },
    fromLineOfLinks: { type: Boolean, default: false },
  },
  data() {
    return {
      activeColor: -1,
      hovering: false,
      isAlertOpen: false,
      isSubscribed: false,
      subscribeError: false,
      loading: false,
      refresh: false,
      PRODUCT_TEXT_ALIGN,
      BUNNY_STREAM_FOLDERS,
    }
  },
  head() {
    if (this.isFirst && this.productDocument.image.top_of_image_is_light) {
      return {
        htmlAttrs: {
          class: ['header--dark'],
        },
      }
    }
    return {}
  },
  computed: {
    ...mapGetters({
      connectedEmail: 'subscriptions/connectedEmail',
      account: 'subscriptions/account',
      isSubscriptionsLoading: 'subscriptions/isLoading',
    }),
    cardClass() {
      if (this.cardType === 'slider') {
        return 'product-card-v2--slider'
      }
      return {
        'is-first': this.isFirst,
        'product-card-v2--grid': this.cardType === 'grid',
        'product-card-v2--agenda': this.isAgenda,
        'product-card-v2--preco': this.isPreco,
        'product-card-v2--home': this.isHome,
        'product-card-v2--stock': this.stock,
        'product-card-v2--full': this.typeDesktop === 'one-per-line',
        'product-card-v2--small': this.typeDesktop === 'three-per-line',
        'product-card-v2--gift-card': this.isGiftCard,
        'product-card-v2--soon-in-sell': this.soonInSell,
        'product-card-v2--link': this.isLink,
      }
    },
    isGiftCard() {
      return this.productDocument.type === TYPES.GIFTCARD
    },
    isAgenda() {
      return this.productDocument.status === STATUS.AGENDA && !this.stock
    },
    isSaisonnier() {
      return this.productDocument.type === TYPES.SAISONNIER
    },
    isPreco() {
      return this.productDocument.status === STATUS.PRECOMMANDE && !this.stock
    },
    isTeasingPeriod() {
      return (
        this.isAgenda &&
        this.productDocument.dates.start !== undefined &&
        this.productDocument.dates.teasing !== undefined &&
        isPast(this.productDocument.dates.teasing) &&
        isFuture(this.productDocument.dates.start)
      )
    },
    isLink() {
      if (this.soonInSell || this.bisOnly) {
        return false
      }

      if (!this.isAgenda) {
        return true
      }

      return this.isTeasingPeriod
    },
    to() {
      if (this.isTeasingPeriod) {
        if (this.productDocument.teasing_link) {
          return this.$contextSanityPath(this.productDocument.teasing_link)
        }

        return this.$contextSanityPath({
          _type: 'product',
          uid: this.productDocument.uid,
        })
      }

      if (this.activeColor >= 0) {
        return {
          path: this.$contextSanityPath(this.productDocument),
          query: { color: this.distinctColors[this.activeColor]._key },
        }
      }

      return this.$contextSanityPath(this.productDocument)
    },
    isEternal() {
      return this.productDocument.type === TYPES.ETERNEL
    },
    isTextPositionLeft() {
      return false
    },
    textAlign() {
      return null
    },
    currentColor() {
      if (
        this.productDocument.colors.length > 0 &&
        this.productDocument.colors[this.activeColor]
      ) {
        return this.productDocument.colors[this.activeColor]
      }
      return null
    },
    currentImage() {
      if (this.overrideImage !== null && this.activeColor === -1) {
        return this.overrideImage
      }

      if (this.currentColor) {
        return this.currentColor.image
      }

      return this.productDocument.image.image
    },
    images() {
      if (this.currentImage) {
        return getProductCardImageList(
          this.currentImage,
          this.cardType === 'slider' ? this.cardType : this.typeDesktop
        )
      }

      return []
    },
    showCompareAtPrice() {
      return (
        this.productDocument.shopify_product.priceRange.minVariantPrice !==
        this.productDocument.shopify_product.compareAtPriceRange.minVariantPrice
      )
    },
    showCountDownStartsIn() {
      if (this.isAgenda && this.productDocument.dates.start) {
        return isFuture(this.productDocument.dates.start)
      }

      return false
    },
    showCountDownEndsIn() {
      if (!this.isAgenda && this.productDocument.dates.end) {
        return (
          isFuture(this.productDocument.dates.end, new Date()) &&
          differenceInDays(this.productDocument.dates.end, new Date()) <= 10
        )
      }

      return false
    },
    showCountDown() {
      return (
        this.isFirst && (this.showCountDownStartsIn || this.showCountDownEndsIn)
      )
    },
    distinctColors() {
      if (
        this.isAgenda &&
        this.productDocument.shopify_product?.colorOptions &&
        this.productDocument.shopify_product?.colorOptions.length > 0
      ) {
        return this.productDocument.shopify_product?.colorOptions
      }

      if (
        this.productDocument.shopify_product.options.length >= 0 ||
        this.productDocument.third_options.layout === 'as_label'
      ) {
        return this.productDocument.colors
      }

      const cc = []

      for (const color of this.productDocument.shopify_product.colorOptions) {
        if (!cc.some((c) => c._key.endsWith(color._key))) {
          const productDocumentColor = this.productDocument.colors.find((c) =>
            c._key.endsWith(color._key)
          )
          if (productDocumentColor) {
            cc.push(productDocumentColor)
          }
        }
      }

      cc.map((c) => {
        const colorId = c._key.split('__').pop()

        c.label = this.productDocument.shopify_product.colorOptions.find(
          (co) => co._key === colorId
        )

        return c
      })

      return cc
    },
    productInventory() {
      if (this.shopifyProduct) {
        return (
          Math.abs(this.shopifyProduct.totalInventory) +
          this.productDocument.stock_initial
        )
      }

      return 0
    },

    initialActiveColor() {
      if (this.targetColor && this.distinctColors.length > 0) {
        const colorIndex = this.distinctColors.findIndex(
          (c) => c._key === this.targetColor
        )

        if (colorIndex >= 0) {
          return colorIndex
        }
      }

      return -1
    },
  },
  beforeDestroy() {
    this.$nuxt.$off(EVENTS.USER_STATE_LOADED)

    if (this.stock) {
      this.$nuxt.$off(EVENTS.PRODUCT_UPDATED)
    }
  },
  created() {
    if (this.overrideImage === null) {
      this.activeColor = this.initialActiveColor
    }
  },
  mounted() {
    if (this.isAgenda) {
      if (
        this.connectedEmail === null ||
        (this.connectedEmail !== '' && this.account.email === null)
      ) {
        this.$nuxt.$on(EVENTS.USER_STATE_LOADED, (_) => {
          this.isSubscribed = this.account.isSubscribed(
            this.productDocument.uid
          )
        })
      } else {
        this.isSubscribed = this.account.isSubscribed(this.productDocument.uid)
      }
    }

    if (this.stock) {
      this.$nuxt.$on(EVENTS.PRODUCT_UPDATED, () => {
        this.refresh = true
      })
    }
  },
  methods: {
    ...mapActions({
      subscribe: 'subscriptions/subscribe',
      unsubscribe: 'subscriptions/unsubscribe',
    }),

    mainComponent() {
      if (!this.isLink) {
        return DefaultDiv
      }

      return 'nuxt-link'
    },

    onMouseLeaveCard() {
      this.hovering = false

      if (this.overrideImage === null) {
        this.activeColor = this.initialActiveColor
      } else {
        this.activeColor = -1
      }
    },

    colorMatterMouseEnter(index) {
      this.activeColor = index
    },
    async colorEnter(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      if (el.classList.contains('product-card__colors')) {
        const buttons = el.getElementsByClassName('colors__matter')

        gsap.from(buttons, {
          opacity: 0,
          x: 20,
          stagger: {
            ease: 'power1.out',
            amount: 0.2,
          },
          duration: 0.4,
          onComplete() {
            return done()
          },
        })
      } else {
        return gsap.from(el, {
          opacity: 0,
          duration: 0.3,
          onComplete() {
            return done()
          },
        })
      }
    },
    async colorLeave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      return gsap.to(el, {
        opacity: 0,
        duration: 0.3,
        onComplete() {
          return done()
        },
      })
    },
    goToColor(color) {
      if (this.soonInSell) {
        return
      }

      this.loading = true
      if (color.includes('__')) {
        const [third, colorValue] = color.split('__')

        const query = {
          ...this.params,
          color: colorValue,
        }

        const thirdVariantKey =
          this.productDocument.shopify_product.options.find(
            (o) => o.position === 3
          )?._key || null

        if (thirdVariantKey) {
          query[thirdVariantKey] = third
        }

        this.$router.push({
          path: this.$contextSanityPath(this.productDocument),
          query,
        })
      }

      this.$router.push({
        path: this.$contextSanityPath(this.productDocument),
        query: { ...this.params, color },
      })
    },

    getColorThumbnailBg(color) {
      return getSanityBunnyImageLink(color.thumbnail, {
        width: 100,
        height: 100,
      })
    },

    onAlertClick(ev) {
      ev.stopPropagation()

      const product = {
        productSlug: this.productDocument.uid,
        productName: this.productDocument.name,
        productId: this.productDocument.shopify_product.id,
        productImage: 'https://',
        location: this.origin,
      }

      if (this.isSubscribed) {
        return this.unsubscribe(product)
          .then(() => {
            this.isSubscribed = this.account.isSubscribed(
              this.productDocument.uid
            )

            this.$track.bisUnSubscribe(this.productDocument.uid, this.origin)

            if (this.bisOnly) {
              this.showToaster(`<div>
                ${this.$t('product.alert.unlove', {
                  produit: this.productDocument.title,
                })}
              </div>
              `)
            } else {
              this.showToaster(`<div>
                ${this.$t('product.alert.deactivated', {
                  produit: this.productDocument.title,
                })}
              </div>
              `)
            }
          })
          .catch((err) => {
            this.$logError.captureException(err)
            this.subscribeError = true
          })
      }

      if (this.connectedEmail === '') {
        this.isAlertOpen = !this.isAlertOpen

        return
      }

      return this.subscribe(product)
        .then(() => {
          this.isSubscribed = this.account.isSubscribed(
            this.productDocument.uid
          )

          this.$nuxt.$emit(EVENTS.ASK_NOTIFICATION, this.origin)
          this.$track.bisSubscribe(
            this.account.email,
            this.productDocument.uid,
            this.origin
          )

          if (this.bisOnly) {
            this.showToaster(`<div>
                ${this.$t('product.alert.love', {
                  produit: this.productDocument.title,
                  email: this.account.email,
                })}
              </div>
              `)
          } else {
            this.showToaster(`<div>
                ${this.$t('product.alert.activated', {
                  produit: this.productDocument.title,
                  email: this.account.email,
                })}
              </div>
              `)
          }
        })
        .catch((err) => {
          this.$logError.captureException(err)
          this.subscribeError = true
        })
    },
    showToaster(text) {
      this.$toasted.clear()
      this.$toasted.show(text, {
        position: 'bottom-center',
        duration: 5000,
        keepOnHover: true,
        action: {
          text: '',
          icon: 'close',
          class: 'toasted__close',
          onClick: (_, toastObject) => {
            toastObject.goAway(0)
          },
        },
      })
    },
    hasSubscribe() {
      this.isAlertOpen = false
      this.isSubscribed = this.account.isSubscribed(this.productDocument.uid)
    },
    hasUnSubscribe() {
      this.isAlertOpen = false
      this.isSubscribed = this.account.isSubscribed(this.productDocument.uid)
    },
    afterCreateAlertEnter() {
      document
        .getElementById(`alert-${this.productDocument.uid}-bis-email`)
        .focus()
    },
  },
}
</script>

<style lang="scss">
.product-card-image-switch-enter-active,
.product-card-image-switch-leave-active {
  transition: opacity 0.8s;
}

.product-card-image-switch-enter,
.product-card-image-switch-leave-to {
  opacity: 0;
}

.product-card-v2--grid {
  position: relative;
  overflow: hidden;
  display: block;
  background: var(--tertiary-color);

  .product-card__price {
    .price {
      .number {
        font-size: var(--color-price-font-size);
        line-height: 1;
      }
    }
  }

  .product-card-v2__video {
    position: absolute;
    top: 50%;
    left: 50%;
    pointer-events: none;
    transform: translate(-50%, -50%);
    width: 150vw;
    height: 100%;
  }

  .product-card-v2__image {
    position: absolute;
    inset: 0;
    z-index: 1;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        180deg,
        RGB(0 0 0 / 0) 61.92%,
        RGB(0 0 0 / 0.35) 88.02%
      );
      z-index: 2;

      @include mq($from: tablet) {
        background: linear-gradient(
          180deg,
          RGB(0 0 0 / 0) 52.92%,
          RGB(0 0 0 / 0.35) 87.49%
        );
      }
    }

    .lazy-image-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      .grid-of-products:not(.grid-of-products--full-images) & {
        picture {
          padding-top: 0;
          height: 100%;
        }
      }

      img {
        max-width: none;
        min-height: 100%;
        min-width: 100%;
        width: auto;
        height: auto;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        transition: all 0.5s ease-in-out;
      }
    }
  }

  .product-card-v2__content {
    position: absolute;
    bottom: 0;
    z-index: 3;
    left: 0;
    right: 0;
    padding: var(--spacing);

    .inner {
      display: flex;
      flex-direction: column;
    }

    @include mq($from: tablet) {
      padding: calc(var(--spacing) * 0.6) calc(var(--spacing) * 1.2);
    }
  }

  .product-card-v2__name {
    font-size: var(--h4-font-size);
    line-height: 1.1;
  }

  .product-card-v2__price {
    @include mq($from: tablet) {
      height: rem(34px);
    }
  }

  .product-card__colors {
    align-self: flex-start;
  }

  .product-card__colors--number {
    @include mq($until: tablet) {
      color: var(--secondary-color-5);
    }
  }

  .product-card-v2__agenda {
    font-size: var(--small-font-size);
    background: transparent;
    border: none;
    padding: 0;
    line-height: 1;
    cursor: pointer;

    .icon {
      width: rem(20px);
      height: rem(20px);
    }
  }

  .product-card-v2__loading {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    background: RGB(0 0 0 / 0.4);
  }

  .colors__matter {
    --line-color: RGB(255 255 255 / 0.7);

    flex-shrink: 0;

    .bg {
      &::after {
        box-shadow: 0 0 0 rem(1px) var(--line-color);
      }
    }

    &.active {
      --line-color: var(--secondary-color-4);

      .bg {
        &::after {
          box-shadow: 0 0 0 rem(2px) var(--line-color);
        }
      }
    }
  }

  .product-card-v2__countdown {
    @include mq($from: tablet) {
      margin-bottom: rem(46px);
    }
  }

  .create-alert:not(.create-alert--inline) {
    position: absolute;
    z-index: 5;
    background: var(--secondary-color);
    color: var(--tertiary-color);
    left: 0;
    right: 0;
    bottom: 0;
    padding: rem($spacing * 2);
    text-align: left;
    cursor: default;

    &__title {
      font-size: var(--h4-font-size);
      margin-bottom: rem($spacing * 0.4);
    }

    .input {
      flex-grow: 1;
    }

    @include mq($until: tablet) {
      form .action {
        width: 100%;
      }
    }

    @include mq($from: tablet) {
      form {
        display: flex;

        .action {
          height: 100%;
          padding-top: 0;
          padding-bottom: 0;

          &__inner {
            height: 100%;
          }

          &__text {
            white-space: nowrap;
          }
        }
      }

      .input {
        margin-right: calc(var(--spacing) * 0.6);
        margin-bottom: 0;
      }
    }
  }

  &.product-card-v2--link {
    @include on-hover-and-focus {
      .product-card-v2__image {
        img {
          transform: translate(-50%, -50%) scale(1.05);
        }
      }
    }
  }

  &.is-first {
    .product-card-v2__image {
      &::after {
        background: linear-gradient(
          180deg,
          RGB(56 24 0 / 0.5) 0%,
          RGB(56 24 0 / 0) 12%,
          RGB(56 24 0 / 0.035) 57.81%,
          RGB(56 24 0 / 0.35) 73.44%
        );

        @include mq($from: tablet) {
          background: linear-gradient(
            180deg,
            RGB(56 24 0 / 0) 60%,
            RGB(56 24 0 / 0.4) 100%
          );
        }
      }
    }
  }

  &.product-card-v2--agenda {
    @include mq($until: tablet) {
      .link--underline span::after {
        --link-underline-transform: scaleX(1);
      }

      .product-card-v2__content {
        padding-bottom: calc(var(--spacing) * 1.5);
      }
    }

    &.is-first {
      &:not(.product-card-v2--link) {
        @include mq($until: tablet) {
          .product-card-v2__content {
            width: 100%;
            padding-bottom: calc(var(--spacing) * 0.8);
            background: linear-gradient(
              180deg,
              RGB(56 24 0 / 0) 0%,
              RGB(56 24 0 / 0.2) 100%
            );
          }
        }

        @include mq($from: tablet) {
          .product-card-v2__content {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;

            .inner {
              align-items: flex-start;
            }
          }
        }
      }
    }
  }

  &.product-card-v2--full {
    .product-card-v2__name {
      font-size: var(--h3-font-size);
    }

    .create-alert {
      @include mq($from: tablet) {
        left: 50%;
      }
    }

    &.is-first {
      .product-card-v2__content {
        padding: 0 var(--spacing) var(--spacing);
        inset: auto 0 0;

        @include mq($until: tablet) {
          .inner {
            align-items: center;
          }
        }

        @include mq($from: tablet) {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          inset: auto calc(var(--spacing) * 2) calc(var(--spacing) * 2);
        }
      }

      .product-card-v2__name {
        font-size: var(--countdown-font-size);
        font-weight: 900;
        line-height: 1;
        text-transform: uppercase;
        margin-left: rem(-2px);

        @include mq($until: tablet) {
          text-align: center;
          line-height: 1.1;
        }
      }

      &.product-card-v2--link {
        .product-card-v2__name {
          margin-bottom: calc(var(--spacing) * 0.2);

          @include mq($from: tablet) {
            margin-bottom: calc(var(--spacing) * 1.4);
          }
        }
      }

      &:not(.product-card-v2--link) {
        .product-card-v2__name {
          @include mq($from: tablet) {
            margin-bottom: calc(var(--spacing) * 0.8);
          }
        }
      }
    }
  }

  &.product-card-v2--small {
    .create-alert {
      @include mq($from: tablet) {
        .input {
          margin-right: 0;
        }

        form {
          display: block;

          .action {
            width: 100%;
            margin-top: var(--spacing);
          }

          .action__inner {
            min-height: rem(45px);
          }
        }
      }
    }
  }

  &.product-card-v2--home {
    .product-card-v2__content {
      @include mq($from: tablet) {
        inset: auto calc(var(--spacing) * 2) calc(var(--spacing) * 3);
      }
    }
  }
}

.product-card-v2--gift-card {
  &.product-card-v2--stock {
    .product-card-v2__content {
      padding-bottom: calc(var(--spacing) * 2.2);
    }
  }

  .product-card-v2__name {
    font-size: var(--h3-font-size);
    margin-bottom: 0;
    text-align: center;
  }
}

.product-card-v2--preco {
  .product-card-v2__price {
    align-items: center;
  }

  .product-card__price__inner {
    display: flex;
    align-items: center;
  }

  .product-card__price__text {
    @include visually-hidden;
  }

  .product-card-v2__content > .inner > * {
    justify-content: flex-start;
  }

  .product-card-v2__name {
    text-align: left;
  }
}

.grid-of-products--full-images {
  .product-card-v2--grid .product-card-v2__image,
  .lazy-image-container {
    position: static;

    .lazy-image-container {
      position: static;
    }
  }
}
</style>
